import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FiCheckCircle, FiEdit3, FiHome, FiInfo, FiMapPin, FiPlus, FiSearch, FiXCircle } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { usePurchasingSettingsSection } from "../usePurchasingSettingsSection";
import { InventoryWorkspaceDialog } from "../InventoryFilterPopup";
import { locationMessages } from "./inventoryLocationMessages";
import "../../PurchasingUi.css";
import "./InventoryLocationsTab.css";

const ENDPOINT = "/api/purchasing/inventory/locations";
const normalizeSearch = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const normalizeCode = (value) => normalizeSearch(value).trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 50);
const hasStockSummary = (location) => typeof location?.has_stock === "boolean" && location?.total_qty != null && Number.isFinite(Number(location.total_qty));
const hasCompleteStockSummary = (location) => hasStockSummary(location) && location.stock_summary_complete !== false;
const hasStock = (location) => Boolean(location?.has_stock) || Math.abs(Number(location?.total_qty) || 0) > 0.000001;

function requestError(error, fallback) {
  const message = error?.response?.data?.error;
  if (error?.response?.data?.code === "PURCHASING_BUSINESS_UNIT_CHANGED") return "unitChanged";
  if (error?.response?.status === 409) return "duplicate";
  if (message === "Cannot disable a location that still has stock") return "stockProtected";
  if (message === "Main Location cannot be disabled") return "mainProtected";
  if (message === "Location name is required") return "nameRequired";
  if (message === "Location code is required") return "codeRequired";
  return fallback;
}

export default function InventoryLocationsTab() {
  const { themeId, languageId } = usePmsTheme();
  const words = locationMessages[languageId] || locationMessages.en;
  const { canView, canEdit, context, isCurrent, requestConfig } = usePurchasingSettingsSection("locations");
  const formId = useId();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [customCode, setCustomCode] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const pending = useRef(null);
  const generation = useRef(0);

  const loadLocations = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setLoadFailed(false);
    if (!canView || !isCurrent()) { setLoading(false); setLocations([]); return; }
    try {
      const { data } = await axios.get(ENDPOINT, { ...requestConfig, params: { include_inactive: true } });
      if (current === generation.current && isCurrent()) setLocations(Array.isArray(data) ? data : []);
    } catch {
      if (current === generation.current) setLoadFailed(true);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [canView, isCurrent, requestConfig]);

  useEffect(() => {
    setLocations([]);
    setEditor(null);
    setStatusTarget(null);
    setError("");
    setNotice("");
    setSearch("");
    setIncludeInactive(false);
    setSaving(false);
    pending.current = null;
    loadLocations();
    return () => { generation.current += 1; pending.current = null; };
  }, [context, loadLocations]);

  const activeCount = locations.filter((location) => location.is_active).length;
  const stockCount = locations.every(hasCompleteStockSummary) ? locations.filter(hasStock).length : "—";
  const query = normalizeSearch(search.trim());
  const visible = locations.filter((location) => (includeInactive || location.is_active)
    && normalizeSearch(`${location.name} ${location.code} ${location.is_main ? words.main : words.countPoint} ${location.is_active ? words.active : words.inactive}`).includes(query));
  const busy = saving || loading || loadFailed;
  const isEditing = Boolean(editor?.id);
  const isMain = Boolean(editor?.is_main);
  const effectiveCode = normalizeCode(customCode ? form.code : form.name);

  const closeDialog = () => {
    if (pending.current) return;
    setEditor(null);
    setStatusTarget(null);
    setError("");
  };

  const openEditor = (location = {}) => {
    if (busy || !canEdit || !isCurrent("update")) return;
    setForm({ name: location.name || "", code: location.code || "" });
    setCustomCode(Boolean(location.id));
    setError("");
    setNotice("");
    setEditor(location);
  };

  // Only the selected record is updated; a failed reload never repeats a successful write.
  const persist = async (payload, locationId, success) => {
    if (pending.current || !canEdit || !isCurrent("update")) return;
    const operation = {};
    pending.current = operation;
    setSaving(true);
    setError("");
    const current = generation.current;
    try {
      const { data } = locationId
        ? await axios.patch(`${ENDPOINT}/${locationId}`, payload, requestConfig)
        : await axios.post(ENDPOINT, payload, requestConfig);
      if (current !== generation.current || !isCurrent("update")) return;
      if (data?.id) setLocations((previous) => locationId
        ? previous.map((location) => String(location.id) === String(locationId) ? { ...location, ...data } : location)
        : [...previous, data]);
      setEditor(null);
      setStatusTarget(null);
      if (!locationId) setSearch("");
      setNotice(success);
      await loadLocations();
    } catch (failure) {
      if (current === generation.current) setError(requestError(failure, "saveError"));
    } finally {
      if (pending.current === operation) {
        pending.current = null;
        setSaving(false);
      }
    }
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (pending.current || !editor || !canEdit || !isCurrent("update")) return;
    const name = form.name.trim().replace(/\s+/g, " ");
    if (!name) { setError("nameRequired"); return; }
    if (!isMain && !effectiveCode) { setError("codeRequired"); return; }
    if (!isMain && locations.some((location) => String(location.id) !== String(editor.id)
      && normalizeCode(location.code) === effectiveCode)) { setError("duplicate"); return; }
    persist({ name, ...(!isMain ? { code: effectiveCode } : {}) }, editor.id, isEditing ? "updated" : "created");
  };

  const handleStatus = () => {
    if (!statusTarget || pending.current || !canEdit || !isCurrent("update")) return;
    if (statusTarget.is_main) { setError("mainProtected"); return; }
    if (statusTarget.is_active && !hasCompleteStockSummary(statusTarget)) { setError("stockUnavailable"); return; }
    if (statusTarget.is_active && hasStock(statusTarget)) { setError("stockProtected"); return; }
    persist({ is_active: !statusTarget.is_active }, statusTarget.id, statusTarget.is_active ? "deactivated" : "activated");
  };

  if (!canView) return <section className="pilcore-purchasing-ui pu-locations" data-theme={themeId}><h1>{words.title}</h1><p role="alert">{words.denied}</p></section>;

  return <section className="pilcore-purchasing-ui pu-locations" data-theme={themeId} aria-labelledby={`${formId}-title`}>
    <header className="pu-locations__header">
      <div><h1 id={`${formId}-title`}>{words.title}</h1><p>{words.description}</p></div>
      {canEdit && <button type="button" className="pu-locations__button pu-locations__button--primary" disabled={busy} onClick={() => openEditor()}>
        <FiPlus aria-hidden="true" />{words.add}
      </button>}
    </header>
    {!canEdit && <p role="status">{words.readOnly}</p>}
    <div className="pu-locations__toolbar">
      <label className="pu-locations__search"><FiSearch aria-hidden="true" />
        <input type="search" aria-label={words.search} placeholder={words.search} value={search} onChange={(event) => setSearch(event.target.value)} />
      </label>
      <label className="pu-locations__inactive"><input type="checkbox" checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)} />
        {words.showInactive}<span>{locations.length - activeCount}</span>
      </label>
      <p className="pu-locations__summary">{activeCount} {words.activeCount} <span aria-hidden="true">·</span> {stockCount} {words.withStockCount}</p>
    </div>
    {notice && <div className="pu-locations__notice" role="status"><FiCheckCircle aria-hidden="true" />{words[notice]}</div>}
    <div className="pu-locations__scroll" tabIndex={0} role="region" aria-label={words.list} aria-busy={loading}>
      {loadFailed ? <div className="pu-locations__empty" role="alert"><FiInfo aria-hidden="true" /><h2>{words.loadError}</h2>
        <button type="button" className="pu-locations__button" onClick={loadLocations}>{words.retry}</button></div>
        : <table className="pu-locations__table">
          <thead><tr><th scope="col">{words.location}</th><th scope="col">{words.code}</th><th scope="col">{words.type}</th>
            <th scope="col">{words.stock}</th><th scope="col">{words.status}</th><th scope="col">{words.actions}</th></tr></thead>
          <tbody>{loading ? <tr><td colSpan={6}><div className="pu-locations__empty" role="status">{words.loading}</div></td></tr>
            : visible.length ? visible.map((location) => <tr key={location.id}>
              <td>{canEdit ? <button type="button" className="pu-locations__name" disabled={saving} onClick={() => openEditor(location)}>{location.name}</button> : location.name}</td>
              <td><span className="pu-locations__code">{location.code || "—"}</span></td>
              <td><span className="pu-locations__type">{location.is_main ? <FiHome aria-hidden="true" /> : <FiMapPin aria-hidden="true" />}{location.is_main ? words.main : words.countPoint}</span></td>
              <td><strong className="pu-locations__quantity">{hasStockSummary(location) ? Number(location.total_qty).toLocaleString(languageId === "es" ? "es-ES" : "en-GB", { maximumFractionDigits: 2 }) : "—"}</strong>
                <small>{!hasCompleteStockSummary(location) ? words.stockUnavailable : hasStock(location) ? words.withStock : words.emptyStock}</small></td>
              <td><span className={`pu-locations__status${location.is_active ? " pu-locations__status--active" : ""}`}>
                {location.is_active ? <FiCheckCircle aria-hidden="true" /> : <FiXCircle aria-hidden="true" />}{location.is_active ? words.active : words.inactive}</span></td>
              <td>{canEdit && <div className="pu-locations__actions">
                <button type="button" className="pu-locations__button" aria-label={`${words.edit}: ${location.name}`} disabled={saving} onClick={() => openEditor(location)}><FiEdit3 aria-hidden="true" />{words.edit}</button>
                {location.is_main ? <span className="pu-locations__protected" title={words.mainProtected}>{words.permanent}</span>
                  : <span title={location.is_active ? !hasCompleteStockSummary(location) ? words.stockUnavailable : hasStock(location) ? words.stockProtected : undefined : undefined}>
                    <button type="button" className={`pu-locations__button${location.is_active ? " pu-locations__button--danger" : ""}`}
                      aria-label={`${location.is_active ? words.deactivate : words.activate}: ${location.name}`}
                      disabled={saving || (location.is_active && (!hasCompleteStockSummary(location) || hasStock(location)))}
                      onClick={() => { if (!isCurrent("update")) return; setStatusTarget(location); setError(""); setNotice(""); }}>
                      {location.is_active ? words.deactivate : words.activate}</button></span>}
              </div>}</td>
            </tr>) : <tr><td colSpan={6}><div className="pu-locations__empty"><FiMapPin aria-hidden="true" /><h2>{locations.length ? words.noMatches : words.noLocations}</h2>
              <p>{locations.length ? words.filterHint : words.emptyHint}</p>
              {locations.length > 0 && <button type="button" className="pu-locations__button" onClick={() => { setSearch(""); setIncludeInactive(true); }}>{words.clearFilters}</button>}
            </div></td></tr>}</tbody>
        </table>}
    </div>
    <footer className="pu-locations__footer"><span>{visible.length} / {locations.length} {words.visible}</span><span><FiInfo aria-hidden="true" />{words.protectionHint}</span></footer>
    {canEdit && editor && <InventoryWorkspaceDialog title={isEditing ? words.editTitle : words.add} onClose={closeDialog} actions={<>
      <button type="button" disabled={saving} onClick={closeDialog}>{words.cancel}</button>
      <button type="submit" form={formId} className="inventory-workspace-dialog__primary" disabled={saving || !form.name.trim() || (!isMain && !effectiveCode)}>{saving ? words.saving : isEditing ? words.save : words.create}</button>
    </>}>
      <form id={formId} className="pu-location-form" onSubmit={handleSave} aria-busy={saving}>
        <p>{isEditing ? words.editHint : words.addHint}</p>
        <label htmlFor={`${formId}-name`}>{words.name}<input id={`${formId}-name`} data-inventory-autofocus name="name" className="form-control" value={form.name}
          onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))} disabled={saving} maxLength={120} required placeholder={words.namePlaceholder} autoComplete="off" /></label>
        <label htmlFor={`${formId}-code`}>{words.code}<input id={`${formId}-code`} name="code" className="form-control" value={customCode ? form.code : effectiveCode}
          onChange={(event) => { setCustomCode(true); setForm((previous) => ({ ...previous, code: event.target.value })); }} onBlur={() => setForm((previous) => ({ ...previous, code: normalizeCode(previous.code) }))}
          disabled={saving || isMain} maxLength={50} required={!isMain} placeholder="BAR" autoComplete="off" aria-describedby={`${formId}-code-hint`} />
          <small id={`${formId}-code-hint`}>{isMain ? words.mainCodeHint : words.codeHint}</small></label>
        {error && <div className="pu-location-form__error" role="alert">{words[error]}</div>}
      </form>
    </InventoryWorkspaceDialog>}
    {canEdit && statusTarget && <InventoryWorkspaceDialog title={statusTarget.is_active ? words.deactivateTitle : words.activateTitle} onClose={closeDialog} actions={<>
      <button type="button" disabled={saving} onClick={closeDialog}>{words.cancel}</button>
      <button type="button" className="inventory-workspace-dialog__primary" disabled={saving} onClick={handleStatus}>{saving ? words.saving : statusTarget.is_active ? words.deactivate : words.activate}</button>
    </>}><div className="pu-location-form"><strong>{statusTarget.name}</strong><p>{statusTarget.is_active ? words.deactivateHint : words.activateHint}</p>
      {error && <div className="pu-location-form__error" role="alert">{words[error]}</div>}
    </div></InventoryWorkspaceDialog>}
  </section>;
}
